import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button as MantineButton, Loader } from '@mantine/core'

import { navigate, routes, useLocation } from '@redwoodjs/router'
import { Metadata, useQuery } from '@redwoodjs/web'

import { GET_PUBLIC_JOB_BOARD } from 'src/graphql/jobBoard.graphql'
import { GET_JOB_LISTING_BY_TRACKING_LINK } from 'src/graphql/trackinglinks.graphql'
import { unformatPhoneNumber } from 'src/lib/phone.utils'
import { cn } from 'src/utils'

import Header from '../DriverApplicationPage/components/Header'
import FormSubmitScreen from '../ShortFormPage/FormSubmitScreen'
import {
  ShortFormJobListing,
  formatLocation,
  formatPay,
  hasJobInfo,
} from '../ShortFormPage/jobInfo.utils'
import JobInfoPanel from '../ShortFormPage/JobInfoPanel'
import ShortFormCard from '../ShortFormPage/ShortFormCard'
import {
  PublicTemplateQuestion,
  sortPublicQuestions,
} from '../ShortFormPage/shortFormQuestions.utils'
import { useIsDesktop } from '../ShortFormPage/useIsDesktop'
import { useShortFormSubmit } from '../ShortFormPage/useShortFormSubmit'

import JobCard, { PublicJobCardData } from './components/JobCard'
import JobRail from './components/JobRail'

interface PublicShortFormPageProps {
  companyId: string
}

interface BoardHrefArgs {
  term?: string
  jobListingId?: string | null
  hash?: string
}

const SEARCH_PLACEHOLDER = 'Search positions'

const IconSearch = () => (
  <svg
    className="h-[15px] w-[15px] flex-none text-doubleNickel-gray-400"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
)

/**
 * The company job board at `/public-short-form/{companyId}`.
 *
 * Desktop is one page: a sticky rail of position cards on the left and the
 * selected job's description *and* interest form inside a single card on the
 * right, with the Apply bar as that card's own sticky bottom row. Nothing
 * navigates away — selection is `?jobListingId=` on this same route.
 *
 * Mobile is a single-column card list that hands off to
 * `/short-form/{trackingLink}`, the page that already serves Indeed and ads.
 */
const PublicShortFormPage = ({ companyId }: PublicShortFormPageProps) => {
  const { search } = useLocation()
  const isDesktop = useIsDesktop()
  const [resumeFile, setResumeFile] = useState<File[]>([])

  const detailRef = useRef<HTMLDivElement>(null)
  const applyRef = useRef<HTMLDivElement>(null)
  // Whether the desktop-link-on-mobile handoff has already been decided; see
  // the effect below.
  const handoffCheckedRef = useRef(false)

  // The bar's button is "Apply now" while reading and "Submit" once the form
  // is in view. One IntersectionObserver drives it; everything renders if it
  // never fires, because false is the reading state.
  const [isAtForm, setIsAtForm] = useState(false)
  // A deep link carrying #apply, or Apply pressed on a card, has to wait for
  // the job's detail query before it has anything to scroll to.
  const [pendingApplyScroll, setPendingApplyScroll] = useState(
    () => typeof window !== 'undefined' && window.location.hash === '#apply'
  )

  const urlParams = useMemo(() => new URLSearchParams(search), [search])
  const urlSearchTerm = urlParams.get('q') ?? ''
  const urlJobListingId = urlParams.get('jobListingId')

  // The job pinned into the URL by scrolling to the form. Held in state as
  // well as in the URL because that pin is written with `replaceState`, which
  // the router does not observe — `urlJobListingId` stays null afterwards, so
  // every later URL write has to remember the pin or silently drop it.
  const [pinnedJobId, setPinnedJobId] = useState<string | null>(null)
  const activeJobListingId = urlJobListingId ?? pinnedJobId

  // Typing has to stay responsive, so the input owns its value and the URL is
  // written behind it (with replace). Reading it back keeps Back/Forward and
  // any external navigation in sync.
  const [searchTerm, setSearchTerm] = useState(urlSearchTerm)
  useEffect(() => {
    setSearchTerm((prev) => (prev === urlSearchTerm ? prev : urlSearchTerm))
  }, [urlSearchTerm])

  const { data: boardData, loading: boardLoading } = useQuery(
    GET_PUBLIC_JOB_BOARD,
    {
      skip: !companyId,
      variables: { companyId },
    }
  )

  const board = boardData?.publicJobBoard
  const companyName: string | null = board?.companyName ?? null
  const cards: PublicJobCardData[] = useMemo(() => board?.cards ?? [], [board])

  // An unknown companyId is a genuine 404 — unlike an unknown jobListingId,
  // which still has a valid company to fall back to.
  useEffect(() => {
    if (!boardLoading && boardData && !boardData.publicJobBoard) {
      // `/404` and a hard location change, matching ShortFormPage:105 — the
      // app has no `/not-found` route, and `navigate` PUSHES, so Back returned
      // to the bad board URL, Apollo answered null from cache and it pushed
      // again. The driver could not get back past it.
      window.location.href = '/404'
    }
  }, [boardData, boardLoading])

  const locations = useMemo(() => {
    const seen = new Set<string>()
    cards.forEach((card) => {
      const location = formatLocation(card.address)
      if (location) {
        seen.add(location)
      }
    })
    return Array.from(seen)
  }, [cards])

  const filteredCards = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) {
      return cards
    }
    return cards.filter(
      (card) =>
        card.title.toLowerCase().includes(term) ||
        (card.subtitle ?? '').toLowerCase().includes(term)
    )
  }, [cards, searchTerm])

  const boardHref = useCallback(
    ({ term, jobListingId, hash }: BoardHrefArgs = {}) => {
      const params = new URLSearchParams()
      if (term) {
        params.set('q', term)
      }
      if (jobListingId) {
        params.set('jobListingId', jobListingId)
      }
      // Preserve the /c phone hint across board navigations.
      const phoneHint = urlParams.get('phone')
      if (phoneHint) {
        params.set('phone', phoneHint)
      }
      const queryString = params.toString()
      return `${routes.publicShortForm({ companyId })}${
        queryString ? `?${queryString}` : ''
      }${hash ?? ''}`
    },
    [companyId, urlParams]
  )

  const shortFormHref = useCallback(
    (card: PublicJobCardData) => {
      const base = routes.shortForm({ trackingLink: card.trackingLink })
      const params = new URLSearchParams()
      const term = searchTerm.trim()
      // boardSearch, not q — `/short-form` restores it as the board's ?q= on
      // the way back, and never reads it as its own state.
      if (term) {
        params.set('boardSearch', term)
      }
      // Carry the /c phone hint through the mobile hand-off.
      const phoneHint = urlParams.get('phone')
      if (phoneHint) {
        params.set('phone', phoneHint)
      }
      const queryString = params.toString()
      return queryString ? `${base}?${queryString}` : base
    },
    [searchTerm, urlParams]
  )

  const selectedCard = urlJobListingId
    ? cards.find((card) => card.jobListingId === urlJobListingId) ?? null
    : null

  // Auto-selecting the first job must NOT write to the URL — the canonical
  // board URL stays clean — so the default lives in state instead. It is
  // committed once and then resolved against the FULL card list, never against
  // `filteredCards`: deriving it from the filtered list meant that typing in
  // the search box after filling the form silently re-pointed the submission
  // at a different listing, sending that job's tracking link.
  const [autoSelectedId, setAutoSelectedId] = useState<string | null>(null)

  useEffect(() => {
    if (urlJobListingId || autoSelectedId) {
      return
    }
    // Seeded from the filtered list so an inbound ?q= still opens a match,
    // but only once — after this the search term cannot move the selection.
    const first = filteredCards[0]
    if (first) {
      setAutoSelectedId(first.jobListingId)
    }
  }, [autoSelectedId, filteredCards, urlJobListingId])

  const activeCard =
    selectedCard ??
    cards.find((card) => card.jobListingId === autoSelectedId) ??
    null

  useEffect(() => {
    if (!urlJobListingId || boardLoading || !board) {
      return
    }
    const match = cards.find((card) => card.jobListingId === urlJobListingId)
    if (!match) {
      // Unknown or paused listing: the company is still valid, so fall back to
      // the board rather than /not-found, and replace so the dead link stays
      // out of history.
      navigate(boardHref({ term: searchTerm }), { replace: true })
      return
    }
    if (!isDesktop && !handoffCheckedRef.current) {
      // A desktop link opened on a phone — resolve the id to its tracking link
      // and hand off, replacing so the stale param doesn't linger.
      //
      // Latched to the first resolution: `isDesktop` is live, so without this
      // narrowing an already-open board below 980px — or opening split-screen
      // — replaced the board entry with the short form. Widening again did not
      // bring it back and Back skipped over it.
      handoffCheckedRef.current = true
      navigate(shortFormHref(match), { replace: true })
      return
    }
    handoffCheckedRef.current = true
  }, [
    board,
    boardHref,
    boardLoading,
    cards,
    isDesktop,
    searchTerm,
    shortFormHref,
    urlJobListingId,
  ])

  // One job's full description and custom questions, fetched only when a card
  // is actually opened — the board query deliberately carries neither.
  const { data: detailData } = useQuery(GET_JOB_LISTING_BY_TRACKING_LINK, {
    variables: { link: activeCard?.trackingLink },
    skip: !isDesktop || !activeCard,
  })

  const detailListing: ShortFormJobListing | null =
    detailData?.jobListingByTrackingLink ?? null

  // Never pair one job's description with another's tracking link: while a
  // newly selected card's query is in flight the previous listing may still be
  // in `data`, and the mutation is attributed from `activeCard`.
  const jobListing =
    detailListing && detailListing.jobListingId === activeCard?.jobListingId
      ? detailListing
      : null

  const publicQuestions: PublicTemplateQuestion[] = useMemo(
    () => sortPublicQuestions(jobListing?.publicQuestions ?? []),
    [jobListing?.publicQuestions]
  )

  // Validation, the createBasicApplicant mutation and the driver/office
  // post-submit branch are shared with /short-form/{trackingLink}.
  const {
    form,
    isOfficeJob,
    loading: loadingSubmitApplicant,
    handleSubmission,
    isSubmitted,
    applicationId,
    turnstile,
  } = useShortFormSubmit({
    jobListing,
    trackingLink: activeCard?.trackingLink ?? '',
    resumeFile,
    publicQuestions,
  })

  // Belt and braces for the same class of bug: if the active job ever changes
  // under a partly-filled form, drop the answers rather than submitting them
  // against a listing the driver never read. An empty form is obvious; a
  // mis-attributed application is not.
  const appliedJobIdRef = useRef<string | null>(null)
  useEffect(() => {
    const id = activeCard?.jobListingId ?? null
    if (appliedJobIdRef.current === id) {
      return
    }
    if (appliedJobIdRef.current !== null) {
      form.reset()
      setResumeFile([])
    }
    appliedJobIdRef.current = id
    // `form` is stable for the lifetime of the hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCard?.jobListingId])

  // The mockup removes the Apply bar entirely in this state: with no
  // description, compensation or requirements there is nothing to scroll past,
  // so a sticky "Apply now ↓" would point at a form already on screen. The
  // form's own Submit becomes the only action.
  const showApplyBar = jobListing ? hasJobInfo(jobListing) : false

  const scrollToApply = useCallback(() => {
    applyRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [])

  useEffect(() => {
    if (!pendingApplyScroll || !jobListing) {
      return
    }
    scrollToApply()
    setPendingApplyScroll(false)
  }, [jobListing, pendingApplyScroll, scrollToApply])

  useEffect(() => {
    // A different job starts at the top of its description, so the bar must
    // say "Apply now" again. The observer below only corrects this on its
    // first async callback, which left a stale Submit on screen until then.
    setIsAtForm(false)
    const node = applyRef.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      return undefined
    }
    const observer = new IntersectionObserver(
      (entries) => setIsAtForm(entries[0]?.isIntersecting ?? false),
      { threshold: 0, rootMargin: '0px 0px -45% 0px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [jobListing?.jobListingId])

  // Reaching the form is as much a commitment as clicking "Apply now", and a
  // driver can do it by simply scrolling. Pin the job the first time the apply
  // section comes into view so a refresh or a shared link mid-application lands
  // on the right listing instead of on whatever cards[0] happens to be.
  //
  // `replaceState`, not `navigate`: Redwood's router calls scrollTo(0, 0) on
  // any change to `search`, and `replace: true` does not avoid it — so pinning
  // threw the driver back to the top of the page at the exact moment they
  // scrolled down to the form. Same reason `handleSearchChange` writes the URL
  // directly. Someone scrolling past shouldn't gain a history entry either.
  useEffect(() => {
    if (!isAtForm || activeJobListingId || !activeCard) {
      return
    }
    setPinnedJobId(activeCard.jobListingId)
    window.history.replaceState(
      null,
      '',
      boardHref({ term: searchTerm, jobListingId: activeCard.jobListingId })
    )
  }, [activeCard, activeJobListingId, boardHref, isAtForm, searchTerm])

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    // Deliberately NOT `navigate`: Redwood's router calls scrollTo(0, 0) on any
    // change to `search`, and `replace: true` does not avoid it — so every
    // keystroke threw a driver reading the description, or filling the form,
    // back to the top of the page. `searchTerm` already drives the UI from
    // local state; the URL write exists only so the filtered board can be
    // shared, which replaceState does without touching scroll or history.
    window.history.replaceState(
      null,
      '',
      boardHref({ term: value, jobListingId: activeJobListingId })
    )
  }

  const handleSelect = (card: PublicJobCardData) => {
    if (!isDesktop) {
      navigate(shortFormHref(card))
      return
    }
    // Synchronously, on the click: the effect below also resets this, but
    // effects run after paint, so the bar would show one frame of Submit.
    setIsAtForm(false)
    setPinnedJobId(null)
    // push, so Back steps between jobs and then to the bare board
    navigate(boardHref({ term: searchTerm, jobListingId: card.jobListingId }))
    detailRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }

  const location = jobListing ? formatLocation(jobListing.address) : null
  const compactPay = jobListing
    ? formatPay(jobListing, { compact: true })
    : null
  const applyBarSummary = jobListing
    ? [jobListing.title, location, jobListing.subtitle]
        .filter(Boolean)
        .join(' · ')
    : ''

  // Mobile puts the count and the cities on one line; desktop's rail splits
  // them across the heading and the line beneath it.
  const mobileSummary = [
    `${cards.length} open position${cards.length === 1 ? '' : 's'}`,
    locations.length > 0 ? locations.join(' & ') : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const searchField = (
    <div
      role="search"
      className="flex items-center gap-2 rounded-lg border border-doubleNickel-gray-300 bg-white px-3 py-[9px] shadow-sm focus-within:border-doubleNickel-brand-500"
    >
      <IconSearch />
      <input
        type="search"
        aria-label={SEARCH_PLACEHOLDER}
        placeholder={SEARCH_PLACEHOLDER}
        value={searchTerm}
        onChange={(event) => handleSearchChange(event.currentTarget.value)}
        className="w-full min-w-0 border-none bg-transparent p-0 text-sm text-doubleNickel-gray-800 outline-none placeholder:text-doubleNickel-gray-400"
      />
    </div>
  )

  const noMatches = (
    <p className="px-0.5 text-[13px] leading-relaxed text-doubleNickel-gray-500">
      No positions match your search.
    </p>
  )

  const renderCards = () =>
    filteredCards.map((card) => (
      <JobCard
        key={card.jobListingId}
        card={card}
        companyName={companyName}
        isSelected={isDesktop && card.jobListingId === activeCard?.jobListingId}
        selectHref={
          isDesktop
            ? boardHref({ term: searchTerm, jobListingId: card.jobListingId })
            : shortFormHref(card)
        }
        showApply={!isDesktop}
        onSelect={handleSelect}
      />
    ))

  return (
    <>
      <Metadata
        title={companyName || 'Open positions'}
        description="Open positions"
      />
      <div className="flex min-h-screen flex-col bg-doubleNickel-gray-50">
        <Header companyId={companyId} />

        {boardLoading && (
          <div className="flex flex-1 flex-col items-center justify-center">
            <Loader size="sm" color="gray" />
          </div>
        )}

        {!boardLoading && isSubmitted && (
          // Office applications end here; drivers are routed to phone
          // verification by useShortFormSubmit and never reach this.
          <FormSubmitScreen
            isOfficeJob={isOfficeJob}
            applicationId={applicationId}
            phone={unformatPhoneNumber(form.values.phone)}
          />
        )}

        {!boardLoading && !isSubmitted && board && cards.length === 0 && (
          <div className="mx-auto w-full max-w-[620px] px-6 py-8">
            <section className="rounded-xl border border-doubleNickel-gray-200 bg-white p-8 text-center">
              <h1 className="text-lg font-extrabold text-doubleNickel-gray-900">
                No positions available
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-doubleNickel-gray-600">
                There are currently no open positions available. Please check
                back later for new opportunities.
              </p>
            </section>
          </div>
        )}

        {!boardLoading && !isSubmitted && cards.length > 0 && isDesktop && (
          // The page scrolls and the rail sticks — no fixed-height shell, no
          // nested scroll region.
          <main className="mx-auto grid w-full max-w-[1320px] grid-cols-[400px_minmax(0,1fr)] items-start gap-[26px] px-6 pb-10 pt-6">
            <JobRail
              positionCount={cards.length}
              locations={locations}
              searchValue={searchTerm}
              onSearchChange={handleSearchChange}
              searchPlaceholder={SEARCH_PLACEHOLDER}
            >
              {filteredCards.length === 0 ? noMatches : renderCards()}
            </JobRail>

            <div ref={detailRef} className="scroll-mt-6">
              {!activeCard && (
                <section className="rounded-xl border border-doubleNickel-gray-200 bg-white p-8">
                  <p className="text-sm leading-relaxed text-doubleNickel-gray-600">
                    No positions match your search. Clear the search to see all{' '}
                    {cards.length} open position
                    {cards.length === 1 ? '' : 's'}.
                  </p>
                </section>
              )}

              {activeCard && !jobListing && (
                <section className="flex items-center justify-center rounded-xl border border-doubleNickel-gray-200 bg-white p-8">
                  <Loader size="sm" color="gray" />
                </section>
              )}

              {activeCard && jobListing && (
                // ONE card holds the description and the form; the Apply bar
                // is this card's own sticky bottom row.
                <section className="rounded-xl border border-doubleNickel-gray-200 bg-white p-8">
                  <JobInfoPanel jobListing={jobListing} unstyled />

                  <div
                    id="apply"
                    ref={applyRef}
                    // No heading or subtitle — the form reads as the
                    // continuation of the description. The rule is kept, so
                    // there is still a visible seam between reading and
                    // filling in.
                    className="mt-[26px] scroll-mt-6 border-t border-doubleNickel-gray-200 pt-[26px]"
                  >
                    {/* No submit button of its own — the bar is the only
                        commit control, so exactly one action exists at any
                        moment and it never moves. */}
                    <ShortFormCard
                      form={form}
                      isOfficeJob={isOfficeJob}
                      resumeFile={resumeFile}
                      setResumeFile={setResumeFile}
                      companyId={jobListing.company?.companyId}
                      companyName={jobListing.company?.companyName}
                      publicQuestions={publicQuestions}
                      loading={loadingSubmitApplicant}
                      onSubmit={handleSubmission}
                      turnstile={turnstile}
                      showSubmit={!showApplyBar}
                      fullWidthSubmit={!showApplyBar}
                    />
                  </div>

                  {/* Negative margins cancel the card's padding so the bar
                      spans it edge to edge and settles flush on its bottom
                      radius at the end of the scroll. */}
                  {showApplyBar && (
                    <div className="sticky bottom-0 z-10 -mx-8 -mb-8 mt-[26px] flex items-center gap-3 rounded-b-xl border-t border-doubleNickel-gray-200 bg-white px-6 py-3">
                      <div className="min-w-0 flex-1">
                        {compactPay && (
                          <div className="truncate text-sm font-bold text-doubleNickel-gray-800">
                            {compactPay}
                          </div>
                        )}
                        {applyBarSummary && (
                          <div className="truncate text-xs text-doubleNickel-gray-500">
                            {applyBarSummary}
                          </div>
                        )}
                      </div>
                      {/* Both controls stay mounted and are toggled with CSS,
                        as the mockup specifies. Conditionally mounting them
                        unmounted the element a keyboard user had just focused
                        — their own scroll triggering the observer — and left
                        no Submit at all if IntersectionObserver never ran. */}
                      <a
                        href={boardHref({
                          term: searchTerm,
                          jobListingId: activeCard.jobListingId,
                          hash: '#apply',
                        })}
                        onClick={(event) => {
                          if (
                            event.metaKey ||
                            event.ctrlKey ||
                            event.shiftKey ||
                            event.altKey ||
                            event.button !== 0
                          ) {
                            return
                          }
                          // The document root carries no `scroll-behavior`, so
                          // the native jump would be instant. Scroll it
                          // ourselves; the href still works without JS.
                          event.preventDefault()
                          // Pin the job before the driver touches the form.
                          navigate(
                            boardHref({
                              term: searchTerm,
                              jobListingId: activeCard.jobListingId,
                              hash: '#apply',
                            })
                          )
                          scrollToApply()
                        }}
                        aria-hidden={isAtForm ? 'true' : undefined}
                        tabIndex={isAtForm ? -1 : undefined}
                        className={cn(
                          'inline-flex h-11 flex-none items-center justify-center whitespace-nowrap rounded-lg bg-doubleNickel-brand-500 px-6 text-sm font-semibold text-white shadow-sm',
                          isAtForm && 'hidden'
                        )}
                      >
                        Apply now <span aria-hidden="true">&darr;</span>
                      </a>
                      <MantineButton
                        classNames={{
                          root: 'bg-doubleNickel-brand-500 rounded-lg h-11 shadow-sm',
                        }}
                        aria-hidden={!isAtForm ? 'true' : undefined}
                        tabIndex={!isAtForm ? -1 : undefined}
                        className={cn('flex-none px-6', !isAtForm && 'hidden')}
                        loading={loadingSubmitApplicant}
                        onClick={handleSubmission}
                      >
                        Submit
                      </MantineButton>
                      {/* The sole commit control changes role and label as the
                        driver scrolls; say so rather than letting it change
                        silently underneath assistive tech. */}
                      <span className="sr-only" role="status">
                        {isAtForm
                          ? 'Application form in view. The button now submits your application.'
                          : ''}
                      </span>
                    </div>
                  )}
                </section>
              )}
            </div>
          </main>
        )}

        {!boardLoading && !isSubmitted && cards.length > 0 && !isDesktop && (
          // Mobile hands off: one column of cards, and tapping one navigates
          // to /short-form/{trackingLink}, which already serves the job info,
          // the form sheet and submission on small screens.
          <main className="flex-1 p-3.5">
            <h1 className="text-[18px] font-extrabold tracking-[-0.015em] text-doubleNickel-gray-900">
              Get your application started today.
            </h1>
            <p
              // Two lines then an ellipsis, as on the desktop rail — a carrier
              // hiring across twenty cities otherwise pushed the search field
              // and every card below the fold. The count leads, so it survives
              // the clamp; the full list stays in the title.
              className="mt-0.5 line-clamp-2 text-sm text-doubleNickel-gray-600"
              title={mobileSummary}
            >
              {mobileSummary}
            </p>
            <div className="mb-3 mt-3.5">{searchField}</div>
            <div className="flex flex-col gap-3">
              {filteredCards.length === 0 ? noMatches : renderCards()}
            </div>
          </main>
        )}
      </div>
    </>
  )
}

export default PublicShortFormPage
